import S from 's-js';
import SArray from 's-array';
import { AssertExtends } from 'lib/assertExtends';
import { filterWrites } from 'lib/filterSignalWrites';
import { notNullish } from 'lib/notNullish';
import { sumSignal, SumSignal } from 'lib/sumSignal';
import TZDate from 'lib/TZDate';
import { tzmomentMod } from 'lib/tzmoment';
import { TZUtils } from 'lib/tzutils';
import { ValidationRule, ValidationRules } from 'lib/clientSideValidation';
import { zipMap } from 'lib/zipMap';

import { AddPortCallRequest } from 'data/models/AddPortCallRequest';
import { Berth } from 'data/models/Berth';
import { BerthReservation } from 'data/models/BerthReservation';
import { ItineraryPoint, ItineraryPointOptions } from 'data/models/ItineraryPoint';
import { MilestoneTypes as MSType } from 'data/models/MilestoneTypes';
import { PortCall } from 'data/models/PortCall';
import { RecurringBatch } from 'data/models/RecurringBatch';
import { RecurringBatchRequest } from 'data/models/RecurringBatchRequest';
import { ScheduleDateEntity as DateEntity, ScheduleDateEntityOptions as DateEntityOptions } from 'data/models/ScheduleDateEntity';
import { ScheduleDateType as SDType } from 'data/models/ScheduleDateType';
import { VesselSide } from 'server/enums/BerthReservation';

import { PCCache } from 'data/PCCache';

import { LocalizationContext } from 'common/models/LocalizationContext';

import { commonScheduleRules } from 'common/services/commonScheduleRules';
import { ScheduleRule } from 'common/services/scheduleChangeEngine';
import {
    DIFFERENT_PIERGROUP_TRANSIT_MINS,
    SAME_PIERGROUP_TRANSIT_MINS
} from 'common/services/scheduleConstants';

import { BerthSelector } from 'portcall/berthselector/model';
import { ProgInitDateTime } from 'portcall/datetimepicker/progInit';
import { PortCallScheduleHeader } from 'portcall/portcallschedule/header';

import { AddPortCallTabModel } from './model';
import { BatchRecurrenceFrequency } from 'server/enums/BatchRecurrenceFrequency';

const DEFAULT_BATCH_LENGTH_WEEKS = 3;

export const BATCH_VALIDATION_KEY_CUSTOM_DAYS = 'Batch.RecurrencePeriod.CustomDays';
export const BATCH_VALIDATION_KEY_STOP_DATE = 'StopDate';

export interface InitReservationParams {
    firstLine? : TZDate;
    lastLine? : TZDate;
    footmark? : number;
    berth? : number;
}

export interface AddPortCallItineraryTabParams {
    PortCall : PortCall;
    Request : AddPortCallRequest;
    ScheduleHeader : PortCallScheduleHeader;
    initReservationParams : InitReservationParams;
}

export type AddPortCallItineraryTab = AssertExtends<ReturnType<typeof AddPortCallItineraryTab>, AddPortCallTabModel>;
export function AddPortCallItineraryTab(
    data : PCCache,
    tzutils : TZUtils,
    LocalizationContext : LocalizationContext,
    {
        PortCall,
        Request,
        ScheduleHeader,
        initReservationParams
    } : AddPortCallItineraryTabParams
) {
    let nextBuilderKey = 0;

    const
        [ scheduleViolationsGetter, scheduleViolationsSetter ] = makeScheduleViolationsCounters(),
        PortArrivalDate = ProgInitDateTime(null, tzutils),
        PortEntry = S(() => PortCall.Port()?.Locations().find(l => l.DefaultPortEntry()) ?? null),
        arrivalPointInit = S<InitEntity<ItineraryPoint>>(([pt, cleanup]) => {
            if (pt === null && PortArrivalDate() && PortEntry()) {
                const arrivalTimeJSON = PortArrivalDate()!.toJSON();
                [pt, cleanup] = AddItineraryPoint(
                    data,
                    PortCall,
                    {
                        Id: 0,
                        IsPortArrival: true,
                        // We have computations below to set LocationId, Arrival, and Departure from upstream
                        // data. But set them all here anyway so that the values are correct as soon as we add
                        // the new point to the portcall itinerary.
                        LocationId: PortEntry()!.Id(),
                        Arrival  : { Estimated: arrivalTimeJSON },
                        Departure: { Estimated: arrivalTimeJSON }
                    },
                    { Id: 0, MilestoneTypeId: MSType.FirstArrival },
                    { Id: 0 }
                );
            }
            S.cleanup(isFinal => isFinal && cleanup?.());
            return [pt, cleanup];
        }, [null, null]),
        PortArrivalPoint = () => arrivalPointInit()[0],
        IsPortArrivalDateValid = () => (
            !PortArrivalPoint()?.ArrivalEntity() ||
            scheduleViolationsGetter(PortArrivalPoint()!.ArrivalEntity()!)() === 0
        ) && (
            !PortArrivalPoint()?.DepartureEntity() ||
            scheduleViolationsGetter(PortArrivalPoint()!.DepartureEntity()!)() === 0
        ),
        departurePointInit = S<InitEntity<ItineraryPoint>>(([pt, cleanup]) => {
            if (pt === null && PortEntry()) {
                [pt, cleanup] = AddItineraryPoint(
                    data,
                    PortCall,
                    // NOTE: Unlike other itinerary points we construct in this file, don't worry about init'ing
                    // Arrival/Departure times correctly here. We have a computation below to handle it, the
                    // logic is a little bit complicated, and not much of anything depends on port departure
                    // times anyway.
                    { Id: 0, IsPortDeparture: true, LocationId: PortEntry()!.Id() },
                    { Id: 0 },
                    { Id: 0 }
                );
            }
            S.cleanup(isFinal => isFinal && cleanup?.());
            return [pt, cleanup];
        }, [null, null]),
        PortDeparturePoint = () => departurePointInit()[0],
        Berths = S(() => Array.from(data.Berths.items())),
        // We'll assign our reservation builders an opaque key.
        builderKeys = SArray<number>([]),
        buildersByKey = new Map<number, ReservationBuilder>(),
        ReservationBuilders = zipMap(
            builderKeys,
            // Remove last key and align w/ right edge of original builderKeys array to pair each key with its
            // prior key.
            { arr: () => builderKeys().slice(0, builderKeys().length - 1), align: 'right' },
            (key, prevKey) => {
                const builder = ReservationBuilder(
                    key,
                    data,
                    Berths,
                    tzutils,
                    PortCall,
                    scheduleViolationsGetter,
                    // pass init params to first builder only
                    S.sample(prevKey) === undefined ? initReservationParams : null,
                    () => prevKey() === undefined ?
                        PortArrivalPoint() :
                        buildersByKey.get(prevKey()!)?.Reservation()?.Itinerary() ?? null,
                    () => {
                        buildersByKey.delete(key);
                        builderKeys.remove(key);
                    }
                );
                buildersByKey.set(key, builder);
                return builder;
            }
        ),
        FirstBuilder = () => ReservationBuilders().length > 0 ? ReservationBuilders()[0] : null;

    // Set PortCall's port from first reservation builder's location
    S(() => FirstBuilder()?.Berth()?.Location() &&
        PortCall.PortId(FirstBuilder()!.Berth()!.Location()!.PortId())
    );

    // Update port arrival and port departure locations as port changes
    S(() => PortEntry() && (
        PortArrivalPoint  ()?.LocationId(PortEntry()!.Id()),
        PortDeparturePoint()?.LocationId(PortEntry()!.Id())
    ));

    // Set times for port arrival point
    S(() => PortArrivalDate() && (
        PortArrivalPoint()?.Arrival  .Estimated(PortArrivalDate()!),
        PortArrivalPoint()?.Departure.Estimated(PortArrivalDate()!)
    ));

    // Set times for port departure point
    S(() => {
        if (PortDeparturePoint()) {
            const builders = ReservationBuilders();
            // Find last builder that has both Berth and LastLine populated.
            let builder : ReservationBuilder | null = null;
            for (let i = builders.length - 1; i >= 0; i--) {
                if (builders[i].Berth() && builders[i].LastLine()) {
                    builder = builders[i];
                    break;
                }
            }
            if (builder) {
                const
                    transitMins = builder.Berth()!.BerthTransitOffset(),
                    portDepartureTime = tzutils.newDate(+builder.LastLine()! + transitMins * 60 * 1000);
                PortDeparturePoint()!.Arrival  .Estimated(portDepartureTime);
                PortDeparturePoint()!.Departure.Estimated(portDepartureTime);
            } else if (PortArrivalDate()) {
                // Silly, but we'll have a port departure point even before we have our first reservation
                // builder, and if we don't set the departure point's dates somehow, then they'll remain at
                // the default value of UNIX timestamp 0, which will likely be less than the port arrival date,
                // thus triggering a bogus schedule rule violation (the rules say reasonably enough that the port
                // departure can't happen before the port arrival). So until we get our first reservation builder,
                // just default the port departure point's dates to the port arrival date.
                PortDeparturePoint()!.Arrival  .Estimated(PortArrivalDate()!);
                PortDeparturePoint()!.Departure.Estimated(PortArrivalDate()!);
            }
        }
    });

    // Start building the first reservation when port arrival date is entered.
    S(() => PortArrivalDate() && ReservationBuilders().length === 0 && AddReservationBuilder());

    // If incoming params include enough detail to compute our initial berth transit offset, then go ahead and
    // start building our first reservation and use its initial values to initialize our port arrival date.
    if (initReservationParams.berth && initReservationParams.firstLine) {
        AddReservationBuilder();
        S(() => {
            if (PortArrivalDate() === null && FirstBuilder()?.Berth() && FirstBuilder()?.FirstLine()) {
                const transitMins = FirstBuilder()!.Berth()!.BerthTransitOffset();
                PortArrivalDate(tzutils.newDate(+FirstBuilder()!.FirstLine()! - transitMins * 60 * 1000));
            }
        });
    }

    // Handle switching btwn FinalDeparture vs ShiftDeparture milestone type as shifts are added/removed.
    S(() => {
        const reservations = ReservationBuilders()
            .map(builder => builder.Reservation())
            .filter(notNullish);
        for (let i = 0; i < reservations.length; i++) {
            reservations[i].Itinerary()?.DepartureEntity()?.MilestoneTypeId(
                i === reservations.length - 1 ? MSType.FinalDeparture : MSType.ShiftDeparture
            );
        }
    });

    // Date validation via delegating to the same commonScheduleRules that drive the date updating behavior on
    // the metro map. Note that I originally tried to do this validation via a ScheduleChangeEngine instance
    // instead of querying our schedule rules directly, but that had two main drawbacks:
    //   1. It was overkill, since all we're after here is a boolean answer of valid vs not valid, not a
    //      proposal for how to revise the dates.
    //   2. The ScheduleChangeEngine currently bakes in the assumption that it's working w/ persisted date
    //      entities since it uses a keying strategy that relies on the dates' Ids being populated. So to use
    //      it here we'd either need to change that keying strategy or give our date entities here dummy Ids.
    //      Both ideas seem yucky.
    let nextRuleId = 0;
    S(() => {
        const rules = commonScheduleRules(PortCall).filter(ScheduleRule.isRule);
        for (let rule of rules) {
            const
                id = nextRuleId++,
                recordViolation = (ruleIds : Set<number>) => ruleIds.add(id),
                forgetViolation = (ruleIds : Set<number>) => (ruleIds.delete(id), ruleIds),
                dates = [rule.a];
            if (rule.b instanceof DateEntity) dates.push(rule.b);
            // Watch rule to record it as satisfied vs unsatisfied.
            S(wasSatisfied => {
                const isSatisfied = rule.isSatisfied();
                if (isSatisfied && !wasSatisfied) {
                    for (let date of dates) {
                        scheduleViolationsSetter(date)(forgetViolation);
                    }
                } else if (wasSatisfied && !isSatisfied) {
                    for (let date of dates) {
                        scheduleViolationsSetter(date)(recordViolation);
                    }
                    S.cleanup(isFinal => {
                        if (isFinal) {
                            // This rule is disappearing. It may or may not be getting replaced by an equivalent
                            // rule, but either way remove it from these dates' sets of violated rule IDs.
                            for (let date of dates) {
                                scheduleViolationsSetter(date)(forgetViolation);
                            }
                        }
                    });
                }
                return isSatisfied;
            }, true);
        }
    });

    const
        CanRepeatPortCall = () =>
            // We use PortArrivalDate to initialize the batch request's StopDate below, so it makes sense to
            // require PortArrivalDate before letting the user request a repeated portcall.
            !!PortArrivalDate() &&
            // We'll also require that the user not be in the middle of building a new reservation. There's no
            // technical reason why we couldn't support requesting a repeated portcall in this case; I think
            // this is more about helping the user focus on one thing at a time.
            ReservationBuilders().length > 0 &&
            !!ReservationBuilders()[ReservationBuilders().length - 1].Reservation(),
        RepeatPortCall = S.value(false),
        BatchRequest = S(() => {
            if (RepeatPortCall()) {
                const
                    batch = new RecurringBatch({
                        Id: 0,
                        RecurrencePeriod: { Frequency: BatchRecurrenceFrequency.Weekly },
                        // Need to set ReferenceNumber to some non-empty value b/c unfortunately our
                        // AddPortCall endpoint returns a 400 response if we don't. This is b/c our base
                        // ReferenceNumber declaration in PortCall.Models\PortCall\RecurringBatch.cs has a
                        // [Required] attribute. See comment re ReferenceNumber in
                        // PortCall.EndPoints\Api\RecurringBatch\ApiRecurringBatch.cs for more on the
                        // tradeoffs involved.
                        ReferenceNumber: '--'
                    }),
                    arrivalDate = S.sample(PortArrivalDate),
                    req = new RecurringBatchRequest({
                        StopDate: arrivalDate ?
                            tzmomentMod(tzutils, arrivalDate, m => m.add(DEFAULT_BATCH_LENGTH_WEEKS, 'weeks')).toJSON() :
                            undefined
                    });
                req.Batch(batch);
                Request.BatchRequest(req);
                S.cleanup(() => Request.BatchRequest(null));
                return req;
            } else {
                return null;
            }
        }),
        BatchValidation = new ValidationRules(
            new ValidationRule(
                BATCH_VALIDATION_KEY_STOP_DATE,
                () =>
                    !BatchRequest()
                 || !PortArrivalDate()
                 || +BatchRequest()!.StopDate() >= +PortArrivalDate()!,
                'Stop date must come after start date.'
            ),
            new ValidationRule(
                BATCH_VALIDATION_KEY_CUSTOM_DAYS,
                () =>
                    !BatchRequest()?.Batch()
                 || BatchRequest()!.Batch()!.RecurrencePeriod.Frequency() !== BatchRecurrenceFrequency.Custom
                 || BatchRequest()!.Batch()!.RecurrencePeriod.CustomDays() !== 0,
                'Days is required'
            ),
            new ValidationRule(
                BATCH_VALIDATION_KEY_CUSTOM_DAYS,
                () =>
                    !BatchRequest()?.Batch()
                 || BatchRequest()!.Batch()!.RecurrencePeriod.Frequency() !== BatchRecurrenceFrequency.Custom
                 || BatchRequest()!.Batch()!.RecurrencePeriod.CustomDays() >= 0,
                'Days must be positive'
            )
        ).validation;

    S(() => BatchRequest()?.Batch() &&
        BatchRequest()!.Batch()!.RecurrencePeriod.CustomDays(
            // When selecting custom batch frequency, default custom days to 1. If selecting any other batch
            // frequency type, set custom days to 0.
            BatchRequest()!.Batch()!.RecurrencePeriod.Frequency() === BatchRecurrenceFrequency.Custom ? 1 : 0
        )
    );

    const CanProceed = S(() =>
        IsPortArrivalDateValid() &&
        ReservationBuilders().length > 0 &&
        ReservationBuilders().every(d => d.CanProceedToNextTab()) &&
        Object.keys(BatchValidation()).length === 0
    );

    return {
        LocalizationContext,
        ScheduleHeader,
        PortArrivalDate,
        PortArrivalPoint,
        IsPortArrivalDateValid,
        ReservationBuilders,
        FirstBuilder,
        CanRepeatPortCall,
        RepeatPortCall,
        BatchRequest,
        BatchValidation,
        CanProceed,
        AddReservationBuilder
    };

    function AddReservationBuilder() {
        builderKeys.push(nextBuilderKey++);
    }
}

class ViolationsData {
    readonly violatedRuleIds : SumSignal<Set<number>>;
    readonly violationCounts : () => number;

    constructor() {
        const
            ruleIds = this.violatedRuleIds = sumSignal(new Set()),
            // An S.value so that even if the ruleIds change, downstream code only gets notified if the total
            // COUNT changes.
            counts  = this.violationCounts = S.value(0);
        // We don't need to worry about disposing this computation ever since nothing will reference either
        // ruleIds or counts once this ViolationsData instance gets GC'd.
        S.root(() => S.on(ruleIds, () => counts(ruleIds().size), undefined, true));
    }
}

function makeScheduleViolationsCounters() {
    const
        map = new WeakMap<DateEntity, ViolationsData>(),
        slot = (d : DateEntity) => {
            let data = map.get(d);
            if (data === undefined) {
                data = new ViolationsData();
                map.set(d, data);
            }
            return data;
        },
        setter = (d : DateEntity) => slot(d).violatedRuleIds,
        getter = (d : DateEntity) => slot(d).violationCounts;
    return [ getter, setter ] as const;
}

export type ReservationBuilder = ReturnType<typeof ReservationBuilder>;
function ReservationBuilder(
    Key : number,
    data : PCCache,
    Berths : () => Berth[],
    tzutils : TZUtils,
    portcall : PortCall,
    scheduleViolationsGetter : (d : DateEntity) => () => number,
    initParams : InitReservationParams | null,
    prevPoint : () => ItineraryPoint | null,
    onDelete : () => void
) {
    const
        // The last berth the user explicitly selected. For now, only relevant up until we have enough info
        // to construct our Reservation.
        UserBerth = filterWrites(S.value<Berth | null>(null), b => b !== null),
        FirstLine = ProgInitDateTime(initParams?.firstLine ?? null, tzutils),
        LastLine  = ProgInitDateTime(initParams?.lastLine  ?? null, tzutils),
        reservationInit = S<InitEntity<BerthReservation>>(([br, cleanup]) => {
            if (br === null && UserBerth() && FirstLine() && LastLine()) {
                br = new BerthReservation({
                    Id: 0,
                    Footmark: {
                        Num: UserBerth()!.Id() === initParams?.berth ?
                            initParams?.footmark ?? 0 :
                            Math.ceil(UserBerth()!.StartMark.Num()),
                        UOM: UserBerth()!.StartMark.UOM()
                    },
                    SideTo: VesselSide.TBD
                });
                let pt;
                [pt, cleanup] = AddItineraryPoint(
                    data,
                    portcall,
                    {
                        Id: 0,
                        // We have computations below to set LocationId, Arrival, and Departure from upstream
                        // data. But set them all here anyway so that the values are correct as soon as we add
                        // the new point to the portcall itinerary.
                        LocationId: UserBerth()!.Id(),
                        Arrival  : { Estimated: FirstLine()!.toJSON() },
                        Departure: { Estimated: LastLine ()!.toJSON() }
                    },
                    { Id: 0, MilestoneTypeId: MSType.BerthArrival },
                    // Construct departure point as a final departure. But if we add a next reservation, we'll
                    // make sure to change this to ShiftDeparture instead.
                    { Id: 0, MilestoneTypeId: MSType.FinalDeparture }
                );
                pt.BerthReservation(br);
                br.Itinerary(pt);
            }
            S.cleanup(isFinal => isFinal && cleanup?.());
            return [br, cleanup];
        }, [null, null]),
        Reservation = () => reservationInit()[0],
        // Once we have a Reservation, give its Berth priority over UserBerth, since we have logic elsewhere
        // to update the Reservation's assigned berth on changes to vessel info and footmarks, in accordance
        // w/ our most-utilized-berth rule.
        Berth = () => Reservation()?.Itinerary()?.Location()?.Berth() ?? UserBerth() ?? null,
        berthSelector = BerthSelector({
            Berths,
            SelectedBerth: (b? : Berth | null) => {
                if (b === undefined) {
                    // Display the ASSIGNED berth, no matter what the user last selected.
                    return Berth();
                } else {
                    return S.freeze(() => S.sample(() => {
                        if (b && b !== Berth()) {
                            Reservation()?.Itinerary()?.LocationId(b.Id());
                            // Set res.Footmark based on selected berth, but only if selecting a different
                            // berth than the current one.
                            Reservation()?.Footmark.Num(b.Id() === initParams?.berth ?
                                initParams.footmark ?? 0 :
                                Math.ceil(b.StartMark.Num())
                            );
                            Reservation()?.Footmark.UOM(b.StartMark.UOM());
                        }
                        return UserBerth(b);
                    }))
                }
            }
        }),
        IsPortValid = () => Berth() === null || Berth()!.Location()?.PortId() === portcall.PortId(),
        IsFirstLineValid = () =>
            !Reservation()?.Itinerary()?.ArrivalEntity() ||
            scheduleViolationsGetter(Reservation()!.Itinerary()!.ArrivalEntity()!)() === 0,
        IsLastLineValid = () =>
            !Reservation()?.Itinerary()?.DepartureEntity() ||
            scheduleViolationsGetter(Reservation()!.Itinerary()!.DepartureEntity()!)() === 0,
        CanProceedToNextTab = () =>
            Reservation() !== null &&
            IsPortValid() &&
            IsFirstLineValid() &&
            IsLastLineValid();

    // If we were passed an initial berth Id, then watch for a berth matching that Id.
    if (initParams?.berth) {
        S(() => UserBerth() === null && UserBerth(data.Berths.index(initParams.berth!)()));
    }

    // Copy line times to underlying data model.
    S(() => FirstLine() && Reservation()?.Itinerary()?.Arrival  .Estimated(FirstLine()!));
    S(() => LastLine () && Reservation()?.Itinerary()?.Departure.Estimated(LastLine ()!));

    // Update first line based on changes to assigned berth and previous point's location and departure time.
    const
        _prevIsPortArrival = () => prevPoint()?.IsPortArrival() ?? null,
        _prevBerth = () => prevPoint()?.Location()?.Berth() ?? null,
        _prevDeparture = () => prevPoint()?.Departure.Estimated() ?? null;
    S(({
        berth: oldBerth,
        firstLine: oldFirstLine,
        prevIsPortArrival: oldPrevIsPortArrival,
        prevBerth: oldPrevBerth,
        prevDeparture: oldPrevDeparture
    }) => {
        const
            berth = Berth(),
            firstLine = FirstLine(),
            prevIsPortArrival = _prevIsPortArrival(),
            prevBerth = _prevBerth(),
            prevDeparture = _prevDeparture();
        if (prevDeparture && (
            berth !== oldBerth
         || prevIsPortArrival !== oldPrevIsPortArrival
         || prevBerth !== oldPrevBerth
         || !TZDate.nullableEq(prevDeparture, oldPrevDeparture)
        )) {
            if (
                berth === oldBerth
             && prevIsPortArrival === oldPrevIsPortArrival
             && prevBerth === oldPrevBerth
             && oldPrevDeparture
             && oldFirstLine
            ) {
                // Only relevant change is to previous point's departure time --> stick w/ the existing transit
                // time.
                FirstLine(tzutils.newDate(+prevDeparture + +oldFirstLine - +oldPrevDeparture));
            } else if (berth) {
                // Otherwise recalculate transit time.
                // TODO: It'd be slick to figure out if this "shift" is really the in (or out) movement from
                // (or to) an anchorage, in which case using the arrival (or departure) berth's
                // BerthTransitOffset would probably make more sense here.
                const transitMins =
                    prevIsPortArrival ? berth.BerthTransitOffset() :
                    prevBerth?.PierGroup() === berth.PierGroup() ? SAME_PIERGROUP_TRANSIT_MINS :
                    DIFFERENT_PIERGROUP_TRANSIT_MINS;
                FirstLine(tzutils.newDate(+prevDeparture + transitMins * 60 * 1000));
            }
        }
        return {
            berth,
            firstLine,
            prevIsPortArrival,
            prevBerth,
            prevDeparture
        };
    }, {
        berth: S.sample(Berth),
        firstLine: S.sample(FirstLine),
        prevIsPortArrival: S.sample(_prevIsPortArrival),
        prevBerth: S.sample(_prevBerth),
        prevDeparture: S.sample(_prevDeparture)
    });

    return {
        Key,
        FirstLine,
        LastLine,
        Reservation,
        Berth,
        IsPortValid,
        BerthSelector: berthSelector,
        IsFirstLineValid,
        IsLastLineValid,
        CanProceedToNextTab,
        Delete: onDelete
    };
}

type InitEntity<T> = readonly [ entity : T | null, cleanup : (() => void) | null ];

function AddItineraryPoint(
    data : PCCache,
    PortCall : PortCall,
    opts : ItineraryPointOptions,
    arrivalOpts : DateEntityOptions,
    departureOpts : DateEntityOptions
) {
    // Do these here b/c there are no other rational choices.
    arrivalOpts.Type = SDType.IPArrival;
    departureOpts.Type = SDType.IPDeparture;

    const
        pt = new ItineraryPoint(opts),
        arrival = new DateEntity(arrivalOpts),
        departure = new DateEntity(departureOpts);

    // Wire up nav props to non-persisted entities.
    pt.PortCall(PortCall);
    pt.Schedule([ arrival, departure ]);
    arrival.PortCall(PortCall);
    arrival.ItineraryPoint(pt);
    departure.PortCall(PortCall);
    departure.ItineraryPoint(pt);

    // Wire up nav props to persisted entities.
    // Pass false for the autoDetach param on all of these attach calls. We'll call detach manually below.
    data.ItineraryPoints.attach(pt, [pt.Location], false);
    data.ScheduleDates.attach(arrival, [arrival.MilestoneType], false);
    data.ScheduleDates.attach(departure, [departure.MilestoneType], false);

    // Add to our PortCall.
    PortCall.Itinerary.push(pt);
    PortCall.Schedule.push(arrival);
    PortCall.Schedule.push(departure);

    return [ pt, cleanup ] as const;

    // Cleanup function for calling code to call to remove this point.
    function cleanup() {
        data.ItineraryPoints.detach(pt, [pt.Location]);
        data.ScheduleDates.detach(arrival, [arrival.MilestoneType]);
        data.ScheduleDates.detach(departure, [departure.MilestoneType]);

        PortCall.Itinerary.remove(pt);
        PortCall.Schedule.remove(arrival);
        PortCall.Schedule.remove(departure);
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/itinerary.ts